import 'rsuite/dist/rsuite-no-reset.min.css'
import 'rsuite/DatePicker/styles/index.css'

import { zodResolver } from '@hookform/resolvers/zod'
import * as Form from '@radix-ui/react-form'
import { useParams } from '@tanstack/react-router'
import { message } from 'antd'
import { FormProvider } from 'antd/es/form/context'
import { MessageInstance } from 'antd/es/message/interface'
import { useRuntimeVariables } from 'app-kit'
import { AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { triggerInspectionEditPipeline } from 'features/dsapi'
import { useRolodexEdit } from 'features/fde'
import { useImportInspectionData } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/useImportInspectionData'
import { useSiteRolodexEntries } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/useSiteRolodexEntries'
import {
  formValidationStyles,
  StyledFormSubmitButton,
} from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'
import {
  DropdownOption,
  GeckoAssetModel,
  GeckoComponent,
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  RolodexEntryJson,
  RolodexEntryPutJson,
} from 'types'

import { EntryFormController } from './EntryFormController'
import { defaultValues, ImportInspectionSchema } from './importInspectionSchema'

const submitInspection = (
  inspectionSlug: string,
  inspectionRawJSON: RolodexEntryJson,
  data: ImportInspectionSchema,
  rolodexPut: (
    entries: RolodexEntryPutJson[]
  ) => Promise<AxiosResponse<unknown, unknown>>,
  messageApi: MessageInstance
) => {
  inspectionRawJSON.id = inspectionSlug

  // Assign tags
  const {
    organization,
    site,
    job,
    unit,
    component,
  }: {
    organization: string
    site: string
    job: string
    unit: string
    component: string
  } = data

  inspectionRawJSON.tags = { organization, site, job, unit, component }

  return rolodexPut([inspectionRawJSON])
    .then(() => {
      return messageApi.success(
        `Successfully imported inspection! Redirecting...`,
        1
      )
    })
    .catch(() => {
      return messageApi.error(
        `Failed to import inspection! Please check permissions and try again.`
      )
    })
}

export type UnitAssetModelPair = {
  unit: GeckoUnit
  assetModel: GeckoAssetModel | undefined
}

const StyledInspectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${formValidationStyles}
`
const StyledHeaderDiv = styled.div`
  padding-bottom: 1rem;
`

const StyledFormRoot = styled(Form.Root)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

function filterAndSort<T>(
  items: T[],
  filterFn: (item: T) => boolean,
  sortFn: (a: T, b: T) => number
): T[] {
  return items.filter(filterFn).toSorted(sortFn)
}

function mapToDropdownOption<T>(
  items: T[],
  getValue: (item: T) => string,
  getLabel: (item: T) => string
): DropdownOption[] {
  return items.map((item) => ({
    value: getValue(item),
    label: (
      <div>
        <span>{getLabel(item)}</span>
      </div>
    ),
  }))
}

export const ImportGeckoInspectionProps: RightSideDrawerProps = {
  title: 'Import Gecko Inspection',
}

export const ImportGeckoInspection = () => {
  const { drawerEntry, drawerState, closeDrawer } = useRightDrawer()

  const { authorizedAxios: axios } = useGeckoAuth()
  const { siteID: routeSiteID } = useParams({ strict: false })

  const { DS_API_URL } = useRuntimeVariables()
  const [siteID, setSiteID] = useState<string>(routeSiteID || '')
  const { organization, sites, organizations, jobs, units, components } =
    useSiteRolodexEntries({
      siteID,
      drawerState,
    })

  const [inspectionSlug, setInspectionSlug] = useState<string>('')
  const [orgID, setOrgID] = useState<string>(organization?.id || '')
  const [unitID, setUnitID] = useState<string>('')

  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const form = useForm<ImportInspectionSchema>({
    mode: 'onBlur',
    resolver: zodResolver(ImportInspectionSchema),
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form

  const { inspections, getInspection } = useImportInspectionData()

  const inspectionOptions: DropdownOption[] = (inspections || []).map(
    (inspection) => ({
      value: inspection.id,
      label: inspection.id,
    })
  )

  const orgOptions: DropdownOption[] = useMemo(() => {
    return mapToDropdownOption(
      organizations.toSorted(GeckoOrganization.DefaultSort),
      (org) => org.id,
      (org) => org.data.name
    )
  }, [organizations])

  const sitesFilteredByOrganization = useMemo(() => {
    const typedSites: GeckoSite[] = [...sites]
    return filterAndSort(
      typedSites,
      (s) => s.tags?.organization === orgID,
      GeckoSite.DefaultSort
    )
  }, [orgID, sites])

  const siteOptions: DropdownOption[] = useMemo(() => {
    return mapToDropdownOption(
      sitesFilteredByOrganization,
      (site) => site.id,
      (site) => site.data.name
    )
  }, [sitesFilteredByOrganization])

  const jobsFilteredBySite = useMemo(() => {
    return jobs.filter((job) => job.tags?.site === siteID)
  }, [siteID, jobs])

  const jobOptions: DropdownOption[] = useMemo(() => {
    return mapToDropdownOption(
      jobsFilteredBySite,
      (job) => job.id,
      (job) => job.id
    )
  }, [jobsFilteredBySite])

  const unitsFilteredBySite = useMemo(() => {
    const typedUnits: GeckoUnit[] = [...units]
    return filterAndSort(
      typedUnits,
      (s) => s.tags?.site === siteID,
      GeckoUnit.DefaultSort
    )
  }, [siteID, units])

  const unitOptions: DropdownOption[] = useMemo(() => {
    return mapToDropdownOption(
      unitsFilteredBySite,
      (unit) => unit.id,
      (unit) => unit.data.name
    )
  }, [unitsFilteredBySite])

  const componentsFilteredByUnit = useMemo(() => {
    const typedComponents: GeckoComponent[] = [...components]
    return filterAndSort(
      typedComponents,
      (s) => s.tags?.unit === unitID,
      GeckoComponent.DefaultSort
    )
  }, [unitID, components])

  const componentOptions: DropdownOption[] = useMemo(() => {
    return mapToDropdownOption(
      componentsFilteredByUnit,
      (component) => component.id,
      (component) => component.data.name
    )
  }, [componentsFilteredByUnit])

  useEffect(() => {
    if (drawerEntry) {
      setValue('inspection_slug', inspectionSlug || '')
      setValue('component', drawerEntry.tags.component || '')
      setValue('job', drawerEntry.tags.job || drawerEntry.id || '')
      setValue('unit', unitID || drawerEntry.tags.unit || '')
      setValue('site', siteID || drawerEntry.tags.site || '')
      setValue('organization', orgID || drawerEntry.tags.organization || '')
    }
  }, [drawerEntry, inspectionSlug, orgID, setValue, siteID, unitID])

  const onInspectionSlugChange = (slug: string) => {
    setInspectionSlug(slug)
  }

  const onOrgChange = (newOrgID: string) => {
    setOrgID(newOrgID)
    setSiteID('')
  }

  const onSiteChange = (newSiteID: string) => {
    setSiteID(newSiteID)
    setUnitID('')
  }

  const onUnitChange = (newUnitID: string) => {
    setUnitID(newUnitID)
  }

  const onSubmit: SubmitHandler<ImportInspectionSchema> = (data) => {
    if (inspectionSlug === '') {
      messageApi.error('Inspection slug is required')
      return
    }
    const inspection = getInspection(inspectionSlug)
    const inspectionRawJSON = inspection?.raw
    if (!inspectionRawJSON) {
      messageApi.error('Invalid inspection data.')
      return
    }

    if (!data.component) {
      messageApi.error(`Component is required.`)
      return
    }

    const isFulcrum = inspection?.data.data_source === 'fulcrum'

    submitInspection(
      inspectionSlug,
      inspectionRawJSON,
      data,
      rolodexPut,
      messageApi
    )
      .then(() => {
        return triggerInspectionEditPipeline(
          inspectionSlug,
          DS_API_URL,
          axios,
          isFulcrum
        )
      })
      .then(() => {
        closeDrawer()
      })
  }

  return (
    <FormProvider {...form}>
      <StyledInspectionContainer>
        {contextHolder}
        <div>
          <StyledHeaderDiv>
            <h3>Import Inspection</h3>
            <h4>Slug: {inspectionSlug}</h4>
          </StyledHeaderDiv>
          <StyledFormRoot onSubmit={handleSubmit(onSubmit)}>
            <EntryFormController
              control={control}
              errors={errors}
              setValue={setValue}
              onOrgChange={onOrgChange}
              onSiteChange={onSiteChange}
              onUnitChange={onUnitChange}
              onInspectionSlugChange={onInspectionSlugChange}
              options={{
                inspectionOptions,
                orgOptions,
                siteOptions,
                jobOptions,
                unitOptions,
                componentOptions,
              }}
            />
            <StyledFormSubmitButton htmlType={'submit'}>
              {'Save'}
            </StyledFormSubmitButton>
          </StyledFormRoot>
        </div>
      </StyledInspectionContainer>
    </FormProvider>
  )
}
